import React from 'react';
import { Events, logEvent } from '../utils/logging/events';
import { Footer } from './shared/Footer';
import { PageHead, PAGES } from '../utils/seo';
import { chipClass } from './chatbox/AnswerText';
import { InstallButton } from './shared/InstallButton';

const sample = 'Does swallowing water while brushing break my fast?';

const topics = [
  {
    name: 'Prayer',
    questions: [
      'How do I pray Qasr when travelling?',
      'Can I pray sitting down if standing is difficult?',
      "What do I do if I doubt how many rak'ahs I prayed?",
    ],
  },
  {
    name: 'Fasting',
    questions: [
      sample,
      'Does using an inhaler break my fast?',
      'Who has to make up missed fasts?',
    ],
  },
  {
    name: 'Purity',
    questions: [
      'What invalidates wudu?',
      'When is ghusl obligatory?',
      'Is it okay to pray with a small amount of blood on my clothes?',
    ],
  },
  {
    name: 'Money and transactions',
    questions: [
      'How does khums work on savings?',
      'Is it allowed to take out a mortgage?',
    ],
  },
  {
    name: 'Family',
    questions: [
      'What are the rights of a wife over her husband?',
      "Do I need my father's permission to marry?",
      'Is it permissible to shake hands with the opposite gender?',
    ],
  },
];

const askLink = (q: string) => `/ask?q=${encodeURIComponent(q)}`;

const FirstPage: React.FC = () => {
  return (
    <div className="h-[100svh] overflow-y-auto overflow-x-hidden relative text-left">
      <div className="lattice absolute inset-x-0 top-0 h-[44rem] pointer-events-none [mask-image:linear-gradient(to_bottom,black,transparent)]" />
      <PageHead page={PAGES.home} />
      <div className="relative mx-auto max-w-5xl px-6">
        <header className="flex items-center justify-between py-6">
          <a href="/" className="font-display text-wordmark">
            Marja<span className="text-peach">AI</span>
          </a>
          <a href="/about" className="text-ui text-muted hover:text-peach">
            About
          </a>
        </header>

        <main>
          <section className="flex flex-col gap-14 pt-16 pb-20 md:pt-28">
            <h1 className="rise balance font-display text-display-xl-sm md:text-display-xl">
              Ask about Shia law.
              <br />
              Get an answer you can check.
            </h1>
            <div className="grid items-center gap-14 md:grid-cols-[1.25fr_1fr]">
              <div>
                <p className="text-body-lg text-muted max-w-md">
                  Every answer quotes the rulings of your Marja' and links to
                  the original text, so you can read it for yourself.
                </p>
                <div className="mt-10 flex flex-col gap-3 sm:flex-row sm:items-start">
                  <a
                    href="/ask"
                    onClick={() => logEvent(Events.ASK_QUESTION_BUTTON)}
                    className="inline-flex items-center justify-center bg-peach text-navy text-button px-9 py-4 rounded-full hover:bg-peach-soft transition-colors"
                  >
                    Ask a question
                  </a>
                  <InstallButton
                    wrapperClassName="flex sm:inline-flex [&>button]:w-full sm:[&>button]:w-auto"
                    className="justify-center rounded-full border border-line px-9 py-4 text-button text-muted transition-colors hover:border-peach hover:text-peach"
                  />
                </div>
              </div>

              <div
                className="rise relative"
                style={{ animationDelay: '0.25s' }}
                aria-hidden="true"
              >
                <div className="flex flex-col gap-6 rounded-container border border-line bg-surface p-5 shadow-float">
                  <div className="self-end max-w-[85%] rounded-container rounded-br-tail bg-bubble-user px-5 py-3 text-body">
                    {sample}
                  </div>
                  <div className="border-l-2 border-peach-line pl-5 text-body">
                    Water that reaches the throat can invalidate the fast if
                    swallowed deliberately <span className={chipClass}>1</span>
                  </div>
                </div>
              </div>
            </div>
          </section>

          <section className="pb-8">
            <h2 className="balance font-display text-heading mb-2">
              What people ask
            </h2>
            <p className="mb-10 max-w-xl text-body text-muted">
              Not sure where to start? Pick a question, or ask your own.
            </p>
            <div className="grid gap-x-14 gap-y-10 md:grid-cols-2">
              {topics.map((t) => (
                <div key={t.name}>
                  <h3 className="mb-2 text-ui text-peach">{t.name}</h3>
                  <ul className="divide-y divide-line border-y border-line !list-none !pl-0">
                    {t.questions.map((q) => (
                      <li key={q}>
                        <a
                          href={askLink(q)}
                          className="block py-4 text-body hover:text-peach transition-colors"
                        >
                          {q}
                        </a>
                      </li>
                    ))}
                  </ul>
                </div>
              ))}
            </div>
            <p className="mt-10 text-caption text-muted-soft max-w-xl">
              Answers are AI-generated and can contain errors. Always check the
              listed sources, your Marja's official website, or ask a scholar.
            </p>
          </section>
        </main>
      </div>
      <Footer />
    </div>
  );
};

export default FirstPage;
